import { ErrorMessage } from "@hookform/error-message";
import * as moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalBody,
  UncontrolledDropdown,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../components/Component";
import Table from "../../components/react-table/Table";
import { DELETE, GET, POST, PUT } from "../../helper/api";
import { getToken } from "../../helper/auth";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { BILL_TYPE, DEFAULT_DELIVERY_PROVIDER_ID, findUpper, INGREDIENT_PRICE_HISTORY_COMMISSION_TYPE, INGREDIENT_PRICE_HISTORY_STATUS, INGREDIENT_PRICE_HISTORY_TARGET_BY, INGREDIENT_PRICE_HISTORY_TARGET_BY_NAME, SETTING_KEY } from '../../utils/Utils';
import RangeDatePicker from "../../components/range-date-picker/RangeDatePicker";
import { IngredientPriceCommissionComponent } from "./IngredientPriceCommissionComponent";


const DEFAULT_INGREDIENT = {
  ingredientId: "",
  customerId: "",
  type: "IN",
  amount: 0,
  quotaWeight: 0,
  status: "INIT",
  targetBy: INGREDIENT_PRICE_HISTORY_TARGET_BY.WEIGHT,
  threshold: 0,
  numOfTruck: undefined,
  note: "",
  targetApprovalBy: "",
  handlerId: null,
  commissions: [],
  deliveryProviderId: null,
  warehouseId: null,
  isNeedPickup: false,
};

const BILL_TYPE_OPTIONS = Object.keys(BILL_TYPE).map((c) => ({
  label: BILL_TYPE[c],
  value: c,
}));

const INGREDIENT_PRICE_HISTORY_STATUS_OPTIONS = Object.keys(INGREDIENT_PRICE_HISTORY_STATUS).map((c) => ({
  label: INGREDIENT_PRICE_HISTORY_STATUS[c],
  value: c,
}));

const INGREDIENT_PRICE_HISTORY_TARGET_BY_OPTIONS = Object.keys(INGREDIENT_PRICE_HISTORY_TARGET_BY).map((c) => ({
  label: INGREDIENT_PRICE_HISTORY_TARGET_BY_NAME[c],
  value: c,
}));

const IngredientPriceManager = () => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    in: {},
    out: {},
    total: {},
    report: {
      in: [],
      out: [],
    }
  });
  const [view, setView] = useState({
    edit: false,
    add: false,
    details: false,
    isDelete: false,
  });
  const form = useForm(
    {
      defaultValues: DEFAULT_INGREDIENT,
      mode: 'onChange'
    }
  );
  const { errors, handleSubmit, reset, getValues, control, watch, setValue } = form

  const targetBy = watch('targetBy');
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [loading, setLoading] = useState(false);
  const [editId, setEditedId] = useState();
  const [deleteId, setDeleteId] = useState();
  const [timeNow, setTimeNow] = useState();
  const [smOption, setSmOption] = useState(false);
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [deliveryProviderOptions, setDeliveryProviderOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [userHandlers, setUserHandlers] = useState([]);
  const [settingCommissionDefault, setSettingCommissionDefault] = useState({
    BHD: 15,
    DTP: 35,
  });
  const [filter, setFilter] = useState({
    customerId: searchParams.get('customerId') || undefined,
    ingredientId: searchParams.get('ingredientId') || undefined,
    status: searchParams.get('status') || 'APPROVED',
    type: searchParams.get('type') || undefined,
  });

  const isNeedPickup = watch('isNeedPickup');
  const ingredientId = watch('ingredientId');
  const formCustomerId = watch('customerId');
  const formWarehouseId = watch('warehouseId');
  const formDeliveryProviderId = watch('deliveryProviderId');
  // Phí vận chuyển/kg tra theo (khách hàng, kho, ĐVVC) — giống BE khi lưu, dùng để hiển thị read-only
  const [previewDeliveryFeeByWeight, setPreviewDeliveryFeeByWeight] = useState(null);
  const [rangeDate, setRangeDate] = useState({
    startDate: moment(searchParams?.get('startDate') || undefined).startOf('months').toDate(),
    endDate: moment(searchParams?.get('endDate') || undefined).endOf('days').toDate(),
  });

  const onRangeChange = (dates) => {
    const [startDate, endDate] = dates;
    setRangeDate({
      startDate: startDate ? moment(startDate)?.startOf('days').toDate() : startDate,
      endDate: endDate ? moment(endDate)?.endOf('days').toDate() : endDate
    });
    if (startDate && endDate) {
      setTimeNow(moment().valueOf());
    }
  };


  const colTable = [
    {
      Header: "Mã",
      accessor: "id",
    },
    {
      Header: "Người duyệt",
      accessor: (item) => item.targetApprovalUser?.name,
    },
    {
      Header: "Người quản lý",
      accessor: (item) => item.handler?.name,
    },
    {
      Header: "Tên khách hàng",
      accessor: (item) => item.customer.name,
    },
    {
      Header: "Tên nguyên liệu",
      accessor: (item) => item.ingredient.name,
    },
    {
      Header: "Giá(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.amount}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Đơn giá net(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.netUnitPrice}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "KL định mức(kg)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.quotaWeight}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "KL hiện tại(kg)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.currentWeight}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "KL còn lại(kg)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.lessWeight}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Giá trị còn lại(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.lessPrice}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Giá trị net còn lại(đ)",
      className: 'text-right',
      // = đơn giá net x KL còn lại (BE tính sẵn: row.netLessPrice)
      accessor: (item) => <NumericFormat
        value={item.netLessPrice}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "KL lệch cho phép(kg)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.threshold}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Xe định mức(xe)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.numOfTruck}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Xe hiện tại(xe)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.currentNumOfTruck}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Xe còn lại(xe)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.lessTruck}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },

    {
      Header: "T.Trạng",
      accessor: (item) => INGREDIENT_PRICE_HISTORY_STATUS[item.status],
    },
    {
      Header: "Loai",
      accessor: (item) => BILL_TYPE[item.type],
    },
    {
      Header: "Ghi chú",
      accessor: "note",
    },
    {
      Header: "Ngày tạo",
      accessor: (item) => moment(item.createdAt).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      Header: "Hoa hồng",
      accessor: (item) => item.commissions.map((c) => <div>
        {findUpper(INGREDIENT_PRICE_HISTORY_COMMISSION_TYPE[c.type])}: <NumericFormat
          value={c.commissionFeePerWeight}
          displayType={'text'}
          decimalScale={0}
          thousandSeparator={','}
          thousandsGroupStyle="thousand"
          suffix="đ"
        />
      </div>),
    },
    {
      Header: "Hành động",
      accessor: (item) => (
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle
                tag="a"
                className="btn btn-icon dropdown-toggle btn-trigger"
              >
                <Icon name="more-h"></Icon>
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setEditedId(item.id);
                        toggle("edit");
                        reset({
                          ...item,
                        });
                      }}
                    >
                      <Icon name="edit" className="text-primary"></Icon>
                      <span>Sửa</span>
                    </DropdownItem>
                  </li>
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setDeleteId(item.id);
                        toggle("isDelete");
                      }}
                    >
                      <Icon name="trash" className="text-danger"></Icon>
                      <span>Xoá</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      ),
    },
  ];

  const colSummary = [
    {
      Header: "Tên nguyên liệu",
      accessor: (item) => item.ingredient.name,
    },
    {
      Header: "Tổng KL(kg)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.totalLessWeight}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Tổng giá trị(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.totalLessPrice}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Tổng giá trị net(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.totalNetLessPrice}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Tổng xe(xe)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.totalLessTruck}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Đơn giá trung bình(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.totalLessUnitPrice}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Đơn giá net trung bình(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.netLessUnitPrice}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
  ]

  useEffect(() => {
    setPreviewDeliveryFeeByWeight(null);
    if (!isNeedPickup || !formCustomerId || !formWarehouseId || !formDeliveryProviderId) {
      return;
    }
    GET(
      `delivery-fee`,
      {
        customerId: formCustomerId,
        warehouseId: formWarehouseId,
        deliveryProviderId: formDeliveryProviderId,
      },
      true
    )
      .then(({ data }) => {
        setPreviewDeliveryFeeByWeight(data?.rows?.[0] ? (data.rows[0].amount || 0) : undefined);
      })
      .catch(() => setPreviewDeliveryFeeByWeight(undefined));
  }, [isNeedPickup, formCustomerId, formWarehouseId, formDeliveryProviderId]);

  useEffect(() => {
    GET(`/setting/${SETTING_KEY.DEFAULT_COMMISSION_INGREDIENT_FEE_EXPORT}`, {}, true)
      .then(({ data }) => {
        console.log('data', data);
        if (data) {
          setSettingCommissionDefault(data.content);
        }
      })
      .catch((err) => {
      })
  }, [])
  useEffect(() => {
    Promise.all([
      GET("ingredient", {}, true),
      GET("customer", {}, true),
      GET(`/user/list-can-confirm-assign-review/${SETTING_KEY.ASSIGN_REVIEWER_INGREDIENT_PRICE_HISTORY}`, {}, true),
      GET("/user/list-can-handler-ingredient-price-history", {}, true),
      GET("/warehouse", {}, true),
      ,

    ])
      .then(([
        { data: ingredient },
        { data: customer },
        { data: userAssignReviewer },
        { data: userHandlers },
        { data: warehouse },
      ]) => {

        setWarehouseOptions(
          warehouse.rows.map((c) => ({
            label: c.name,
            value: c.id,
            data: c,
          }))
        );
        setIngredientOptions(
          ingredient.rows.map((c) => ({
            label: c.name,
            value: c.id,
            data: c,
          }))
        );
        setCustomerOptions(
          customer.map((c) => ({
            label: c.name,
            value: c.id,
            data: c,
          }))
        );
        setDeliveryProviderOptions(
          customer.filter(c => c.isDeliveryProvider).map((c) => ({
            label: c.name,
            value: c.id,
            data: c,
          }))
        );
        setUserOptions(
          userAssignReviewer.map((c) => ({
            label: c.name,
            value: c.id,
            data: c,
          }))
        );

        setUserHandlers(
          userHandlers.map((c) => ({
            label: c.name,
            value: c.id,
            data: c,
          }))
        );

      });
  }, []);

  // Changing state value when searching name
  useEffect(() => {
    const newParams = new URLSearchParams({
      startDate: moment(rangeDate.startDate).format("YYYY-MM-DD"),
      endDate: moment(rangeDate.endDate).format("YYYY-MM-DD"),
      ...(filter.customerId ? { customerId: filter.customerId } : {}),
      ...(filter.ingredientId ? { ingredientId: filter.ingredientId } : {}),
      ...(filter.status ? { status: filter.status } : {}),
      ...(filter.type ? { type: filter.type } : {}),

    });
    history.replace({
      pathname: location.pathname,
      search: newParams.toString(),
    })

    setLoading(true);
    setData([]);
    let params = {
      startDate: moment(rangeDate.startDate).format("YYYY-MM-DD"),
      endDate: moment(rangeDate.endDate).format("YYYY-MM-DD"),
      ...(filter.customerId ? { customerId: filter.customerId } : {}),
      ...(filter.ingredientId ? { ingredientId: filter.ingredientId } : {}),
      ...(filter.type ? { type: filter.type } : {}),
      ...(filter.status ? { status: filter.status } : {}),
    };

    GET(`ingredient-price-history`, params, true)
      .then(({ data: { rows, summary } }) => {
        setData([...rows]);
        setSummary(summary);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filter, timeNow]);

  useEffect(() => {
    setValue('commissions', []);
    console.log('ingredientId', settingCommissionDefault?.[ingredientId]);
    if (ingredientId && settingCommissionDefault?.[ingredientId] !== undefined) {
      setValue('commissions', [
        {
          type: 'BONUS_FUND',
          commissionFeePerWeight: settingCommissionDefault[ingredientId],
          description: `Hoa hồng mặc định cho ${ingredientId} bán ra`,
        }
      ]);
    }
  }, [ingredientId]);
  // toggle function to view order details
  const toggle = (type) => {
    setView({
      ...view,
      [type]: !view[type],
    });
  };

  const resetData = () => {
    setEditedId("");
    onFormCancel();
    setTimeNow(moment().valueOf());
  };

  // Submits form data
  const onHandleSubmit = () => {
    setLoading(true);
    const { amount,
      ingredientId,
      customerId,
      note,
      type,
      quotaWeight,
      status,
      targetBy,
      threshold,
      numOfTruck,
      targetApprovalBy,
      handlerId,
      commissions,
      isNeedPickup,
      deliveryProviderId,
      warehouseId,
    } = getValues();

    const formData = {
      amount,
      ingredientId,
      customerId,
      note,
      type,
      quotaWeight,
      status,
      targetBy,
      threshold,
      numOfTruck,
      targetApprovalBy,
      handlerId,
      commissions,
      isNeedPickup,
      deliveryProviderId: deliveryProviderId || null,
      warehouseId: warehouseId || null,
    };

    const request = editId
      ? PUT(`ingredient-price-history/${editId}`, formData, {}, true)
      : POST("ingredient-price-history", formData, {}, true);

    request
      .then((res) => {
        if (editId) {
          toast.success("Cập nhật thành công");
        } else {
          toast.success("Thêm thành công");
        }
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // function to close the form modal
  const onFormCancel = () => {
    setView({ add: false, edit: false, isDelete: false });
    reset(DEFAULT_INGREDIENT);
  };

  const deleteOrder = (id) => {
    if (!id) {
      return;
    }
    DELETE(`ingredient-price-history/${id}`, {}, true)
      .then((res) => {
        toast.success("Xóa giá nguyên liệu thành công");
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  };

  const exportExcel = () => {
    const params = new URLSearchParams({
      startDate: moment(rangeDate.startDate).format("YYYY-MM-DD"),
      endDate: moment(rangeDate.endDate).format("YYYY-MM-DD"),
      ...(filter.customerId ? { customerId: filter.customerId } : {}),
      ...(filter.ingredientId ? { ingredientId: filter.ingredientId } : {}),
      ...(filter.type ? { type: filter.type } : {}),
      ...(filter.status ? { status: filter.status } : {}),
      token: getToken(),
    })
    const url = `${process.env.REACT_APP_API_URL}/api/v1/ingredient-price-history/find-all-excel?${params.toString()}`;
    window.open(url, '_blank')
  };

  return (
    <React.Fragment>
      <Head title="Quản lý giá nguyên liệu"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Giá nguyên liệu</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <div className="form-control-wrap">
                        <RangeDatePicker
                          selected={rangeDate.startDate}
                          startDate={rangeDate.startDate}
                          onChange={onRangeChange}
                          endDate={rangeDate.endDate}
                          maxDate={new Date()}
                          selectsRange
                          className="form-control date-picker"
                        />
                      </div>
                    </li>
                    <li>
                      <div
                        className="form-control-wrap"
                        style={{ width: "200px" }}
                      >
                        <RSelect
                          placeholder="Trạng thái"
                          isClearable
                          options={INGREDIENT_PRICE_HISTORY_STATUS_OPTIONS}
                          value={
                            INGREDIENT_PRICE_HISTORY_STATUS_OPTIONS.find(
                              (c) => c.value === filter.status
                            ) || null
                          }
                          onChange={(val) => {
                            setFilter({
                              ...filter,
                              status: val?.value || undefined,
                            })
                          }

                          }
                        />
                      </div>
                    </li>
                    <li>
                      <div className="form-control-wrap" style={{ width: 150 }}>
                        <RSelect
                          isClearable
                          placeholder='Loại'
                          options={BILL_TYPE_OPTIONS}
                          value={BILL_TYPE_OPTIONS.find((c) => c.value === filter.type) || null}
                          onChange={(val) => setFilter({
                            ...filter,
                            type: val?.value || undefined,
                          })}
                        />
                      </div>
                    </li>
                    <li>
                      <div
                        className="form-control-wrap"
                        style={{ width: "200px" }}
                      >
                        <RSelect
                          isClearable
                          placeholder="Nguyên liệu"
                          options={ingredientOptions}
                          value={
                            ingredientOptions.find(
                              (c) => c.value == filter.ingredientId
                            ) || null
                          }
                          onChange={(val) => {
                            setFilter({
                              ...filter,
                              ingredientId: val?.value || undefined,
                            })
                          }

                          }
                        />
                      </div>
                    </li>
                    <li>
                      <div
                        className="form-control-wrap"
                        style={{ width: "200px" }}
                      >
                        <RSelect
                          isClearable
                          placeholder="Khách hàng/Nhà cung cấp"
                          options={customerOptions}
                          value={
                            customerOptions.find(
                              (c) => c.value == filter.customerId
                            ) || null
                          }
                          onChange={(val) => {
                            setFilter({
                              ...filter,
                              customerId: val?.value || undefined,
                            })
                          }

                          }
                        />
                      </div>
                    </li>
                    <li>
                      <div>
                        <Button
                          className="btn-primary"
                          color="primary"
                          onClick={() => {
                            resetData();
                            toggle("add");
                          }}
                        >
                          <Icon name="plus"></Icon>
                          <span>Thêm giá nguyên liệu</span>
                        </Button>
                        <Button onClick={exportExcel} className="ml-3 btn-secondary">
                          Xuất excel
                        </Button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div
                id="DataTables_Table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4 no-footer"
              >
                <div className="my-3">
                  <h5 className="text-center">Danh sách đơn giá nguyên liệu</h5>

                  <Table
                    loading={loading}
                    columns={colTable}
                    data={data}
                    notShowPagi={true}
                    headerRow={(
                      <tr className="nk-tb-item odd font-weight-bold">
                        <td className="nk-tb-col" colSpan={7}>
                          <span>Tổng cộng</span>
                        </td>
                        <td className="nk-tb-col text-right">
                          <NumericFormat
                            value={summary.total.totalQuotaWeight || 0}
                            displayType={'text'}
                            decimalScale={2}
                            thousandSeparator={','}
                            thousandsGroupStyle="thousand"
                          />

                        </td>
                        <td className="nk-tb-col text-right">
                          <NumericFormat
                            value={summary.total.totalCurrentWeight || 0}
                            displayType={'text'}
                            decimalScale={2}
                            thousandSeparator={','}
                            thousandsGroupStyle="thousand"
                          />

                        </td>
                        <td className="nk-tb-col text-right">
                          <NumericFormat
                            value={summary.total.totalLessWeight || 0}
                            displayType={'text'}
                            decimalScale={2}
                            thousandSeparator={','}
                            thousandsGroupStyle="thousand"
                          />
                        </td>
                        <td className="nk-tb-col text-right">
                          <NumericFormat
                            value={summary.total.totalLessPrice || 0}
                            displayType={'text'}
                            decimalScale={2}
                            thousandSeparator={','}
                            thousandsGroupStyle="thousand"
                          />
                        </td>
                        <td className="nk-tb-col text-right">
                          <NumericFormat
                            value={summary.total.totalNetLessPrice || 0}
                            displayType={'text'}
                            decimalScale={2}
                            thousandSeparator={','}
                            thousandsGroupStyle="thousand"
                          />
                        </td>
                        <td></td>

                        <td className="nk-tb-col text-right">
                          <NumericFormat
                            value={summary.total.totalQuotaTruck || 0}
                            displayType={'text'}
                            decimalScale={2}
                            thousandSeparator={','}
                            thousandsGroupStyle="thousand"
                          />
                        </td>
                        <td className="nk-tb-col text-right">
                          <NumericFormat
                            value={summary.total.totalCurrentTruck || 0}
                            displayType={'text'}
                            decimalScale={2}
                            thousandSeparator={','}
                            thousandsGroupStyle="thousand"
                          />
                        </td>
                        <td className="nk-tb-col text-right">
                          <NumericFormat
                            value={summary.total.totalLessTruck || 0}
                            displayType={'text'}
                            decimalScale={2}
                            thousandSeparator={','}
                            thousandsGroupStyle="thousand"
                          />
                        </td>
                        <td colSpan={5}></td>
                      </tr>
                    )}
                  ></Table>
                </div>
                <div className="my-3">
                  <Row>
                    <Col size={12} lg={6}>
                      <h5 className="text-center">Bảng báo cáo nguyên liệu chưa nhập</h5>
                      <Table
                        loading={loading}
                        columns={colSummary}
                        data={summary?.report?.in || []}
                        notShowPagi={true}
                        headerRow={(
                          <tr className="nk-tb-item odd font-weight-bold">
                            <td className="nk-tb-col">
                              <span>Tổng cộng</span>
                            </td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.in.totalLessWeight || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />

                            </td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.in.totalLessPrice || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />
                            </td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.in.totalNetLessPrice || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />
                            </td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.in.totalLessTruck || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />
                            </td>
                            <td></td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.in.netLessUnitPrice || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />
                            </td>
                          </tr>
                        )}
                      ></Table>
                    </Col>
                    <Col size={12} lg={6}>
                      <h5 className="text-center">Bảng báo cáo nguyên liệu chưa xuất</h5>
                      <Table
                        loading={loading}
                        columns={colSummary}
                        data={summary?.report?.out || []}
                        notShowPagi={true}
                        headerRow={(
                          <tr className="nk-tb-item odd font-weight-bold">
                            <td className="nk-tb-col">
                              <span>Tổng cộng</span>
                            </td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.out.totalLessWeight || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />

                            </td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.out.totalLessPrice || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />
                            </td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.out.totalNetLessPrice || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />
                            </td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.out.totalLessTruck || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />
                            </td>
                            <td></td>
                            <td className="nk-tb-col text-right">
                              <NumericFormat
                                value={summary.out.netLessUnitPrice || 0}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={','}
                                thousandsGroupStyle="thousand"
                              />
                            </td>
                          </tr>
                        )}
                      ></Table>
                    </Col>
                  </Row>

                </div>

              </div>
            </div>
          </div>
        </Block>

        <Modal
          isOpen={view.add || view.edit}
          toggle={() => onFormCancel()}
          className="modal-dialog-centered"
          size="xl"
        >
          <ModalBody>
            <a href="#cancel" className="close">
              {" "}
              <Icon
                name="cross-sm"
                onClick={(ev) => {
                  ev.preventDefault();
                  onFormCancel();
                }}
              ></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">{editId ? "Cập nhật giá nguyên liệu" : "Thêm giá nguyên liệu"}</h5>
              <div className="mt-4">
                <FormProvider {...form}>
                  <form onSubmit={handleSubmit(onHandleSubmit)}>
                    <Row className="g-3">
                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Nguyên liệu<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="ingredientId"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, name, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  name={name}
                                  options={ingredientOptions}
                                  value={ingredientOptions.find(
                                    (c) => c.value === value
                                  )}
                                  onChange={(val) => onChange(val.value)}
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="ingredientId"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Khách hàng<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="customerId"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, name, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  name={name}
                                  options={customerOptions}
                                  value={customerOptions.find(
                                    (c) => c.value === value
                                  )}
                                  onChange={(val) => onChange(val.value)}
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="customerId"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Loại đơn<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="type"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, name, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  name={name}
                                  options={BILL_TYPE_OPTIONS}
                                  value={BILL_TYPE_OPTIONS.find(
                                    (c) => c.value === value
                                  )}
                                  onChange={(val) => onChange(val.value)}
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="type"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Đơn giá(đ)
                            <span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="amount"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, ref }) => (
                                <NumericFormat
                                  className="form-control"
                                  inputRef={ref}
                                  type="text"
                                  value={value}
                                  thousandsGroupStyle="thousand"
                                  thousandSeparator=","
                                  decimalScale={2}
                                  onValueChange={(val) =>
                                    onChange(val.floatValue)
                                  }
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="amount"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Loại định mức<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="targetBy"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, name, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  name={name}
                                  options={INGREDIENT_PRICE_HISTORY_TARGET_BY_OPTIONS}
                                  value={INGREDIENT_PRICE_HISTORY_TARGET_BY_OPTIONS.find(
                                    (c) => c.value === value
                                  )}
                                  onChange={(val) => onChange(val.value)}
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="targetBy"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            KL định mức(kg)<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="quotaWeight"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, ref }) => (
                                <NumericFormat
                                  className="form-control"
                                  inputRef={ref}
                                  type="text"
                                  value={value}
                                  thousandsGroupStyle="thousand"
                                  thousandSeparator=","
                                  decimalScale={2}
                                  onValueChange={(val) =>
                                    onChange(val.floatValue)
                                  }
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="quotaWeight"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            KL lệch cho phép(kg) <span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="threshold"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, ref }) => (
                                <NumericFormat
                                  className="form-control"
                                  inputRef={ref}
                                  type="text"
                                  value={value}
                                  thousandsGroupStyle="thousand"
                                  thousandSeparator=","
                                  decimalScale={2}
                                  onValueChange={(val) =>
                                    onChange(val.floatValue)
                                  }
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="threshold"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Số xe định mức(xe)
                            {targetBy === INGREDIENT_PRICE_HISTORY_TARGET_BY.TRUCK && <span className="text-danger">*</span>}
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="numOfTruck"
                              rules={targetBy === INGREDIENT_PRICE_HISTORY_TARGET_BY.TRUCK ? { required: "Vui lòng nhập thông tin" } : {}}
                              render={({ onChange, value, ref }) => (
                                <NumericFormat
                                  className="form-control"
                                  inputRef={ref}
                                  type="text"
                                  value={value}
                                  thousandsGroupStyle="thousand"
                                  thousandSeparator=","
                                  decimalScale={2}
                                  onValueChange={(val) =>
                                    onChange(val.floatValue)
                                  }
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="numOfTruck"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Người duyệt<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="targetApprovalBy"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  options={userOptions}
                                  value={userOptions.find(
                                    (c) => c.value === value
                                  )}
                                  onChange={(val) => onChange(val.value)}
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="targetApprovalBy"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Người quản lý
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="handlerId"
                              render={({ onChange, value, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  options={userHandlers}
                                  value={userHandlers.find(
                                    (c) => c.value === value
                                  ) || null}
                                  onChange={(val) => onChange(val?.value ?? null)}
                                  isClearable
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="handlerId"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Trạng thái<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="status"
                              rules={{ required: "Vui lòng nhập thông tin" }}
                              render={({ onChange, value, name, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  name={name}
                                  options={INGREDIENT_PRICE_HISTORY_STATUS_OPTIONS}
                                  value={INGREDIENT_PRICE_HISTORY_STATUS_OPTIONS.find(
                                    (c) => c.value === value
                                  )}
                                  onChange={(val) => onChange(val.value)}
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="status"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col size="12" md="3">
                        <div className="form-group">
                          <label className="form-label" >
                            Cần đi giao/lấy<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="isNeedPickup"
                              render={({ onChange, value, ref }) => (
                                <div className="custom-control custom-switch">
                                  <Input
                                    id="isNeedPickupSwitch"
                                    className="custom-control-input"
                                    inputRef={ref}
                                    type="checkbox"
                                    checked={!!value}
                                    onClick={() => {
                                      const newValue = !value;
                                      onChange(newValue);
                                      if (newValue === true) {
                                        setValue('deliveryProviderId', DEFAULT_DELIVERY_PROVIDER_ID);
                                        setValue('warehouseId', warehouseOptions[0]?.value || null);
                                      } else {
                                        setValue('deliveryProviderId', null);
                                        setValue('warehouseId', null);
                                      }

                                    }}
                                  />
                                  <label
                                    className="custom-control-label"
                                    for="isNeedPickupSwitch"
                                  ></label>
                                </div>
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="isNeedPickup"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col size="12" md="3" className={isNeedPickup ? '' : 'd-none'}>
                        <div className="form-group">
                          <label className="form-label" >
                            Dịch vụ vận chuyển <small className="text-muted">(dùng cho tính giá net)</small><span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="deliveryProviderId"
                              rules={isNeedPickup ? { required: "Vui lòng nhập thông tin" } : {}}
                              render={({ onChange, value, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  options={deliveryProviderOptions}
                                  value={deliveryProviderOptions.find(
                                    (c) => c.value === value
                                  ) || null}
                                  onChange={(val) => onChange(val.value)}
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="deliveryProviderId"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col size="12" md="3" className={isNeedPickup ? '' : 'd-none'}>
                        <div >
                          <label className="form-label" >
                            Kho<span className="text-danger">*</span>
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="warehouseId"
                              rules={isNeedPickup ? { required: "Vui lòng nhập thông tin" } : {}}
                              render={({ onChange, value, ref }) => (
                                <RSelect
                                  inputRef={ref}
                                  options={warehouseOptions}
                                  value={warehouseOptions.find(
                                    (c) => c.value === value
                                  ) || null}
                                  onChange={(val) => onChange(val.value)}
                                />
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="warehouseId"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col size="12" md="3" className={isNeedPickup ? '' : 'd-none'}>
                        <div className="form-group">
                          <label className="form-label" >
                            Phí vận chuyển (đ/kg)
                          </label>
                          <div className="form-control-wrap">
                            <NumericFormat
                              className="form-control"
                              value={previewDeliveryFeeByWeight ?? ''}
                              placeholder={previewDeliveryFeeByWeight === undefined ? 'Chưa cấu hình phí vận chuyển' : ''}
                              thousandSeparator={","}
                              decimalScale={2}
                              readOnly
                              disabled
                            />
                          </div>
                        </div>
                      </Col>
                      <Col size="12" md="12">
                        <div className="form-group">
                          <label className="form-label" >
                            Ghi chú
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="note"
                              render={({ onChange, value, ref }) => (
                                <textarea
                                  className="form-control"
                                  style={{ minHeight: "30px" }}
                                  ref={ref}
                                  value={value}
                                  onChange={(e) => onChange(e.target.value)}
                                ></textarea>
                              )}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="note"
                              render={({ message }) => (
                                <span className="invalid">{message}</span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col size={12}>
                        <IngredientPriceCommissionComponent customerOptions={customerOptions} />
                      </Col>
                      <Col size="12">
                        <Button color="primary" type="submit">
                          <span>Lưu</span>
                        </Button>
                      </Col>
                    </Row>
                  </form>
                </FormProvider>
              </div>
            </div>
          </ModalBody>
        </Modal>
        <Modal isOpen={view.isDelete} toggle={() => onFormCancel()}>
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <Icon className="nk-modal-icon icon-circle icon-circle-xxl ni ni-cross bg-danger"></Icon>
              <h4 className="nk-modal-title">
                Bạn có chắc muốn xoá giá nguyên liệu này không ?{" "}
              </h4>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    deleteOrder(deleteId);
                  }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default IngredientPriceManager;
